import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  IconButton,
  Divider
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { UtilityService } from '../../types/utility.types';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface ViewUtilityServiceDialogProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  service: UtilityService;
  propertyId: string;
  clientId: string;
}

const ViewUtilityServiceDialog: React.FC<ViewUtilityServiceDialogProps> = ({
  open,
  onClose,
  onEdit,
  onDelete,
  service,
  propertyId,
  clientId,
}) => {
  const navigate = useNavigate();
  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'Not set';
    return format(new Date(date), 'dd/MM/yyyy');
  };

  const Field = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
    <Grid item xs={12} md={6}>
      <Typography variant="subtitle2" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body1">
        {value ?? 'Not set'}
      </Typography>
    </Grid>
  );
  
  // Helper function to format numeric values with proper units
  const formatNumericValue = (value: number | undefined, unit: string) => {
    if (value === undefined || value === null) return 'Not set';
    return `${value} ${unit}`;
  };
  
  // Check if service is energy type (gas or electricity)
  const isEnergyService = service.serviceType === 'Gas' || service.serviceType === 'Electricity';

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 25, 25, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
          Utility Service Details
          <div>
            <IconButton 
              onClick={() => {
                onClose();
                // Navigate to the contract edit form
                navigate(`/utilities/contracts/${service._id}/edit?propertyId=${propertyId}&clientId=${clientId}`);
              }} 
              color="primary" 
              size="small" 
              sx={{ mr: 1 }}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={onDelete} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3, color: 'white' }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Field label="Service Type" value={service.serviceType} />
            <Field label="Account Number" value={service.accountNumber} />
            <Field label="Status" value={service.status} />
            <Field label="Contract Start Date" value={formatDate(service.contractStartDate)} />
            <Field label="Contract End Date" value={formatDate(service.contractEndDate)} />
            
            {/* Meter Details Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Meter Details</Typography>
            </Grid>
            <Field label="Serial Number" value={service.meterDetails?.serialNumber} />
            <Field 
              label={service.serviceType === 'Gas' ? 'MPRN' : 
                    service.serviceType === 'Electricity' ? 'MPAN' : 
                    service.serviceType === 'Water' ? 'SPID' : 'Meter Identifier'} 
              value={service.meterDetails?.mpanMprnSpid} 
            />
            
            {/* Rate Information Section - Only for energy services */}
            {isEnergyService && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Rate Information</Typography>
                </Grid>
                <Field label="Current Rate" value={service.currentRate} />
                <Field label="EAC/AQ" value={service.eacAq} />
                <Field label="Standing Charge" value={formatNumericValue(service.standingCharge, 'p/day')} />
                <Field label="Uplift" value={formatNumericValue(service.uplift, 'p')} />
                
                {/* Day Rate & Consumption */}
                <Field label="Day Rate" value={formatNumericValue(service.dayRate, 'p/kWh')} />
                <Field label="Day Consumption" value={formatNumericValue(service.dayConsumption, 'kWh')} />
                
                {/* Night Rate & Consumption */}
                <Field label="Night Rate" value={formatNumericValue(service.nightRate, 'p/kWh')} />
                <Field label="Night Consumption" value={formatNumericValue(service.nightConsumption, 'kWh')} />
                
                {/* Weekend Rate & Consumption */}
                <Field label="Weekend Rate" value={formatNumericValue(service.weekendRate, 'p/kWh')} />
                <Field label="Weekend Consumption" value={formatNumericValue(service.weekendConsumption, 'kWh')} />
              </>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Additional Information</Typography>
            </Grid>
            <Field label="Notes" value={service.notes} />
          </Grid>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={onClose}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Removed EditUtilityServiceDialog in favor of direct navigation to ContractForm */}
    </>
  );
};

export default ViewUtilityServiceDialog;
